// Debug Service — Azure Function diagnostics (Key Vault connectivity, etc.)
import { authService } from '../auth/authService';
import { probeCanadaHolidaysApi } from '../utils/whosIn/canadaHolidays';

class DebugService {
  constructor() {
    // Use dedicated wolfwalker-api Function App (Python Flex Consumption with VNet)
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken('qgisApi');
      const userInfo = authService.getUserInfo();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown'
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Authenticated GET to a wolfwalker-api debug endpoint.
   * Returns { ok, status, statusText, body } (body is parsed JSON when possible).
   */
  async fetchDebugEndpoint(path) {
    const headers = await this.getAuthenticatedHeaders();
    const url = `${this.functionAppUrl}/api/${path}`;
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    const text = await response.text();
    let body;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { _parseError: true, raw: text };
    }
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      body,
    };
  }

  /** GET /api/kv_connectivity_check — KV_CONNECTIVITY_TEST Key Vault reference. */
  async checkKeyVaultConnectivity() {
    return this.fetchDebugEndpoint('kv_connectivity_check');
  }

  /**
   * GET /api/drillz_connectivity_check — DRILLZ_TENANT_CONN_STR + sample from
   * tenant_data.v_drillz_campaign_progress.
   */
  async checkDrillzTenantConnection() {
    return this.fetchDebugEndpoint('drillz_connectivity_check');
  }

  /**
   * GET /api/drillz_geometry_debug?hole_id= — tenant_geometry diagnostic for 3D viewer.
   */
  async checkDrillzGeometry(holeId) {
    const id = String(holeId || '').trim();
    if (!id) throw new Error('hole_id is required');
    return this.fetchDebugEndpoint(`drillz_geometry_debug?hole_id=${encodeURIComponent(id)}`);
  }

  /**
   * GET /api/drillz_list_values_debug — tenant_app list_definitions catalog + quicklog list_code probe.
   * @param {string} tableCode
   * @param {string} fieldCodesCsv
   */
  async checkDrillzListValues(tableCode = 'quicklog', fieldCodesCsv = 'rock_type,min_style,min_type') {
    const table = String(tableCode || 'quicklog').trim() || 'quicklog';
    const fields = String(fieldCodesCsv || '').trim() || 'rock_type,min_style,min_type';
    const query = [
      `table_code=${encodeURIComponent(table)}`,
      `field_codes=${encodeURIComponent(fields)}`,
    ].join('&');
    return this.fetchDebugEndpoint(`drillz_list_values_debug?${query}`);
  }

  /**
   * Public canada-holidays.ca probe (same stack as Who’s In — {@link fetchProvinceHolidays}).
   * Does not require Azure auth or app token.
   */
  async probeCanadianPublicHolidays(year, provinceId = 'BC') {
    const y =
      year === undefined || year === null || year === ''
        ? new Date().getFullYear()
        : year;
    return probeCanadaHolidaysApi(y, provinceId);
  }

  /**
   * GET /api/procurify_bills_debug — Procurify AP bills (server OAuth; wlfwkr-debug group).
   * @param {{ assigneeEmail?: string, page?: number, pageSize?: number, mode?: 'connectivity'|'bills', includeRaw?: boolean }} [options]
   */
  async fetchProcurifyBills(options = {}) {
    const params = new URLSearchParams();
    const mode = options.mode === 'connectivity' ? 'connectivity' : 'bills';
    if (mode === 'connectivity') {
      params.set('mode', 'connectivity');
    } else {
      const email = String(options.assigneeEmail || '').trim();
      if (email) {
        params.set('assignee_email', email);
      }
      if (options.page != null && options.page !== '') {
        params.set('page', String(options.page));
      }
      if (options.pageSize != null && options.pageSize !== '') {
        params.set('page_size', String(options.pageSize));
      }
      if (options.includeRaw) {
        params.set('include_raw', 'true');
      }
    }
    const qs = params.toString();
    return this.fetchDebugEndpoint(
      qs ? `procurify_bills_debug?${qs}` : 'procurify_bills_debug'
    );
  }
}

// Export singleton instance
export const debugService = new DebugService();
export default debugService;
